/**
 * src/components/feature/viewer/ViewerResumeEngine.js
 * 
 * Standardized Continuity Engine for all Viewers (Netflix-style Resume).
 * Handles:
 * 1. Auto-Saving Progress (Debounced)
 * 2. Smart Restoration (<95% scroll, >=95% next/reset)
 * 3. Cross-Device Synchronization
 * 4. Polling for reliability (Image-heavy content)
 */
import { HistoryManager } from "../../../history.js";

export const ViewerResumeEngine = {
    _saveTimeout: null,
    _scrollHandler: null,

    /**
     * Initialize Resume Engine for a session
     * @param {HTMLElement|Window} scrollTarget - The element to listen to scroll events on
     * @param {Object} context - Metadata { seriesId, episodeId, title, thumbnail, seriesTitle, authorId }
     * @param {Object} navigation - { episodeIndex }
     */
    init: (scrollTarget, context, navigation) => {
        // Clear previous
        ViewerResumeEngine.destroy(scrollTarget);

        const { seriesId, episodeId, title, thumbnail, seriesTitle } = context;
        const { episodeIndex } = navigation;

        const handleScroll = () => {
            if (ViewerResumeEngine._saveTimeout) clearTimeout(ViewerResumeEngine._saveTimeout);

            ViewerResumeEngine._saveTimeout = setTimeout(() => {
                if (!window.user) return; // Guest check

                // Calculate Progress
                let scrollTop, scrollHeight, clientHeight;
                if (scrollTarget === window) {
                    scrollTop = window.scrollY || document.documentElement.scrollTop;
                    scrollHeight = document.documentElement.scrollHeight;
                    clientHeight = window.innerHeight;
                } else {
                    scrollTop = scrollTarget.scrollTop;
                    scrollHeight = scrollTarget.scrollHeight;
                    clientHeight = scrollTarget.clientHeight;
                }

                // Avoid division by zero
                if (scrollHeight <= clientHeight) return;

                const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;

                // Save via HistoryManager
                HistoryManager.saveReadingProgress(window.user, {
                    id: seriesId,
                    title: seriesTitle,
                    thumbnail: thumbnail || ''
                }, {
                    id: episodeId,
                    order: episodeIndex,
                    scrollPosition: Math.floor(scrollTop),
                    progress: Math.min(100, Math.max(0, progress))
                });

            }, 1000); // 1s Debounce
        };

        ViewerResumeEngine._scrollHandler = handleScroll;
        scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    },

    /**
     * Restore reading position with Polling/Retry for image-heavy content.
     * @param {HTMLElement|Window} scrollTarget 
     * @param {string} seriesId 
     * @param {string} episodeId 
     * @returns {Promise<boolean>} true if restored
     */
    restore: async (scrollTarget, seriesId, episodeId) => {
        if (!window.user || !window.user.readHistory) return false;

        const history = window.user.readHistory[seriesId];
        if (!history) return false;

        // Check Match
        if (String(history.lastEpisodeId) !== String(episodeId)) return false;

        // Continuity Rule: 95% Completion
        // If > 95%, treat as "Finished" -> Start from Top
        if ((history.progress || 0) >= 95) {
            console.log("[ResumeEngine] Content > 95% read. Resetting to top.");
            return false;
        }

        const targetPos = history.scrollPosition || 0;
        if (targetPos <= 0) return false;

        console.log(`[ResumeEngine] Attempting restore to ${targetPos}px`);

        // Robust Restoration Logic (Polling)
        return new Promise((resolve) => {
            let attempts = 0;
            const maxAttempts = 30; // Try for ~3 seconds
            let hasToastShown = false;

            const tryScroll = () => {
                const currentHeight = (scrollTarget === window)
                    ? document.documentElement.scrollHeight
                    : scrollTarget.scrollHeight;

                const clientHeight = (scrollTarget === window)
                    ? window.innerHeight
                    : scrollTarget.clientHeight;

                // Try scrolling
                if (scrollTarget === window) {
                    window.scrollTo({ top: targetPos, behavior: 'auto' });
                } else {
                    scrollTarget.scrollTop = targetPos;
                }

                // Check result
                const currentPos = (scrollTarget === window)
                    ? (window.scrollY || document.documentElement.scrollTop)
                    : scrollTarget.scrollTop;

                // Success criteria: 
                // 1. We are close to target (< 20px tolerance)
                // 2. OR we are at bottom (and target was beyond bottom, implying layout shift)
                const isCloseEnough = Math.abs(currentPos - targetPos) < 20;
                const isAtBottom = (currentPos + clientHeight) >= (currentHeight - 5);

                // Note: isAtBottom alone is tricky if content is super short. 
                // Only consider isAtBottom if targetPos was actually deeper than current.
                const stuckAtBottom = isAtBottom && (targetPos > currentPos);

                if (isCloseEnough || stuckAtBottom) {
                    if (!hasToastShown && window.Modal && window.Modal.toast) {
                        window.Modal.toast("이전 위치에서 이어봅니다.");
                        hasToastShown = true;
                    }
                    resolve(true); // Done
                } else {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(tryScroll, 100);
                    } else {
                        console.warn("[ResumeEngine] Restore timed out (Layout issues or user intervention?)");
                        resolve(false);
                    }
                }
            };

            // Start polling
            tryScroll();
        });
    },

    destroy: (scrollTarget) => {
        if (ViewerResumeEngine._scrollHandler && scrollTarget) {
            scrollTarget.removeEventListener('scroll', ViewerResumeEngine._scrollHandler);
        }
        if (ViewerResumeEngine._saveTimeout) clearTimeout(ViewerResumeEngine._saveTimeout);
    }
};
